import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import EmptyState from "../../../components/ui-components/EmptyState";
import StatusPill from "../../Budgets/components/StatusPill";
import { fmtEur } from "./format";

const AT_RISK_THRESHOLD = 0.9;

function statusFor(projected, cap) {
  if (cap <= 0) return "green";
  if (projected > cap) return "over";
  if (projected > cap * AT_RISK_THRESHOLD) return "at_risk";
  return "green";
}

/**
 * BudgetComparisonBlock - projected spend vs. cap for a single budget.
 *
 * GET /api/v1/forecast-explorer/budgets/{id}?horizon= (bare) -> { trajectory: [
 *   { date, forecast, budget } ] }. `budget` is a constant daily-prorated cap
 * repeated per remaining-period day (cmd/api/forecast_explorer_handlers.go
 * GetExplorerBudgetDetail); the trajectory only covers the days remaining in
 * the period (tomorrow through period end) - there is no full-period cap in
 * the response. Binding default: projected = sum(trajectory[].forecast),
 * cap = sum(trajectory[].budget), both over the identical remaining-days
 * window (apples-to-apples). Status is computed client-side (the endpoint
 * returns none) reusing the green/at_risk/over vocabulary from StatusPill:
 * over if projected > cap, at_risk if projected > 90% of cap.
 */
const BudgetComparisonBlock = ({ title = "Budget vs. cap", binding = {}, period }) => {
  const { budgetId, horizon = 90 } = binding;
  const { data, isLoading, error } = useBlockData(
    budgetId ? `/api/v1/forecast-explorer/budgets/${encodeURIComponent(budgetId)}` : null,
    { params: budgetId ? { horizon } : undefined }
  );

  const { projected, cap, status } = useMemo(() => {
    const trajectory = data?.trajectory ?? [];
    const p = trajectory.reduce((sum, pt) => sum + (pt.forecast ?? 0), 0);
    const c = trajectory.reduce((sum, pt) => sum + (pt.budget ?? 0), 0);
    return { projected: p, cap: c, status: statusFor(p, c) };
  }, [data]);

  if (!budgetId) {
    return (
      <BaseBlock title={title} isLoading={false} error={null}>
        <EmptyState
          icon={icon({ name: "wallet", style: "light" })}
          title="No budget bound"
          message="Bind a budget to this block to show projected spend vs. cap."
        />
      </BaseBlock>
    );
  }

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error}>
      <div className="a4-budget-comparison">
        <div className="a4-budget-comparison-row">
          <span className="a4-budget-comparison-label">Projected</span>
          <span className="a4-budget-comparison-value">{fmtEur(projected)}</span>
        </div>
        <div className="a4-budget-comparison-row">
          <span className="a4-budget-comparison-label">Cap</span>
          <span className="a4-budget-comparison-value">{fmtEur(cap)}</span>
        </div>
        <StatusPill status={status} />
      </div>
    </BaseBlock>
  );
};

BudgetComparisonBlock.propTypes = {
  title: PropTypes.string,
  binding: PropTypes.shape({
    budgetId: PropTypes.string,
    horizon: PropTypes.number,
  }),
  period: PropTypes.string,
};

export default BudgetComparisonBlock;
